import { api } from '@/shared/api/client'
import { url, type QueryValue } from '@/shared/lib/query'
import type {
  AdminSessionResponse, CreateUserRequest, CursorPage, InvitationIssuedResponse,
  PageResponse, RevokeAllResponse, SessionSummaryResponse, UpdateUserRequest,
  UserResponse, UserRoleResponse, UserSummaryResponse,
} from '@/shared/api/types'

// ── Users ───────────────────────────────────────────────────────────────────

export interface UserFilters {
  q: string
  status: string[]
  scopeLevel: string[]
  clearance: string[]
  roleId: string
  neverLoggedIn: boolean
  locked: boolean
  sort: string
}

export const EMPTY_USER_FILTERS: UserFilters = {
  q: '', status: [], scopeLevel: [], clearance: [], roleId: '',
  neverLoggedIn: false, locked: false, sort: '',
}

/**
 * `sort` is an allowlist server-side (`name`, `email`, `createdAt`, `lastLoginAt`,
 * `status`) and an unknown field is a 400, not a 500 — so the column headers here
 * must only ever emit one of those five.
 */
export function userParams(f: UserFilters): Record<string, QueryValue> {
  return {
    q: f.q,
    status: f.status,
    scopeLevel: f.scopeLevel,
    clearance: f.clearance,
    roleId: f.roleId,
    neverLoggedIn: f.neverLoggedIn ? true : '',
    locked: f.locked ? true : '',
    sort: f.sort,
  }
}

export const listUsers = (f: UserFilters, page = 0, size = 50) =>
  api.get<PageResponse<UserResponse>>(url('/users', { ...userParams(f), page, size }))

export const usersSummary = (f: UserFilters) =>
  api.get<UserSummaryResponse>(url('/users/summary', userParams(f)))

export const getUser = (id: string) => api.get<UserResponse>(`/users/${id}`)

export const createUser = (body: CreateUserRequest) =>
  api.post<UserResponse>('/users', body)

export const updateUser = (id: string, version: number, body: UpdateUserRequest) =>
  api.patch<UserResponse>(`/users/${id}`, body, { headers: { 'If-Match': String(version) } })

export const changeStatus = (id: string, status: string) =>
  api.post<UserResponse>(`/users/${id}/status`, { status })

export const changeScope = (id: string, scopeLevel: string, scopeUnitId?: string | null) =>
  api.put<UserResponse>(`/users/${id}/scope`, { scopeLevel, scopeUnitId: scopeUnitId ?? null })

export const issueInvitation = (id: string) =>
  api.post<InvitationIssuedResponse>(`/users/${id}/invitation`)

export const requestPasswordReset = (id: string) =>
  api.post<InvitationIssuedResponse>(`/users/${id}/password-reset`)

// ── Mobile PIN ──────────────────────────────────────────────────────────────
//
// Two pairs, not one with an optional id. Setting your OWN pin re-proves your password and
// needs no permission; setting somebody else's needs `user:set-pin` and is a different
// decision entirely — collapsing them into one call would hide that.

export const setMyPin = (pin: string, currentPassword: string) =>
  api.put<void>('/users/me/pin', { pin, currentPassword })

export const clearMyPin = () => api.del<void>('/users/me/pin')

export const setUserPin = (id: string, pin: string) =>
  api.put<void>(`/users/${id}/pin`, { pin })

export const clearUserPin = (id: string) => api.del<void>(`/users/${id}/pin`)

// ── Roles held by a user ────────────────────────────────────────────────────

export const userRoles = (userId: string) =>
  api.get<UserRoleResponse[]>(`/rbac/users/${userId}/roles`)

export const setUserRoles = (userId: string, roleIds: string[]) =>
  api.put<unknown>(`/rbac/users/${userId}/roles`, { roleIds })

// ── Sessions ────────────────────────────────────────────────────────────────

export interface SessionFilters {
  userId: string
  q: string
  activeOnly: boolean
  ip: string
}

export const EMPTY_SESSION_FILTERS: SessionFilters = {
  userId: '', q: '', activeOnly: true, ip: '',
}

export const listAdminSessions = (f: SessionFilters, cursor?: string, limit = 50) =>
  api.get<CursorPage<AdminSessionResponse>>(url('/admin/sessions', {
    userId: f.userId,
    q: f.q,
    // Sent explicitly: the server default is true, and "show history" must be
    // able to turn it OFF rather than merely omit it.
    activeOnly: f.activeOnly,
    ip: f.ip,
    cursor,
    limit,
  }))

export const adminSessionSummary = () =>
  api.get<SessionSummaryResponse>('/admin/sessions/summary')

/** Revoking your own current session needs `confirmSelf=true` — see §5 of prompt 3. */
export const revokeAdminSession = (familyId: string, reason?: string, confirmSelf = false) =>
  api.del<void>(url(`/admin/sessions/${familyId}`, { confirmSelf: confirmSelf ? true : '' }), {
    body: reason ? { reason } : undefined,
  })

export const revokeAllForUser = (userId: string) =>
  api.post<RevokeAllResponse>(`/admin/users/${userId}/sessions/revoke-all`)

/** Your own sessions — no elevated permission needed. */
export const revokeOwnSession = (familyId: string) =>
  api.del<void>(`/auth/sessions/${familyId}`)

// ── Devices bound to an account ─────────────────────────────────────────────
//
// The screen somebody opens when a phone is stolen. See MOBILE-APP-PLAN.md §4.8.

export interface DeviceBinding {
  id: string
  userId: string
  label?: string | null
  platform: string
  model?: string | null
  osVersion?: string | null
  appVersion?: string | null
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REVOKED'
  /** PLAY_INTEGRITY | APP_ATTEST | NONE. NONE is refused in production. */
  attestation?: string | null
  activatedBy?: string | null
  activatedAt?: string | null
  revokedAt?: string | null
  revokeReason?: string | null
  lastSeenAt?: string | null
  createdAt: string
}

export const listUserDevices = (userId: string) =>
  api.get<DeviceBinding[]>(`/devices/user/${userId}`)

/**
 * The code is read out of band — over a call, in person — and typed here. It is
 * what proves the person holding the phone is the person who asked for it, so
 * this endpoint is the second half of a two-person act, not a formality.
 */
export const activateDevice = (deviceId: string, code: string) =>
  api.post<DeviceBinding>(`/devices/${deviceId}/activate`, { code })

export const revokeDevice = (deviceId: string, reason?: string) =>
  api.post<DeviceBinding>(`/devices/${deviceId}/revoke`, reason ? { reason } : {})

export const suspendDevice = (deviceId: string, reason?: string) =>
  api.post<DeviceBinding>(`/devices/${deviceId}/suspend`, reason ? { reason } : {})

export const restoreDevice = (deviceId: string) =>
  api.post<DeviceBinding>(`/devices/${deviceId}/restore`)

/**
 * Delete the binding so the handset enrols again from scratch.
 *
 * Not revoke. A revoked binding still EXISTS, so the phone reads it back and parks on "no
 * longer activated" — right for a stolen handset, a dead end for one whose binding was
 * destroyed underneath it by a database reload. Forgetting removes the row, the phone's next
 * poll 404s, and it returns to sign-in where a fresh activation code can be issued.
 *
 * Needs `device:admin`, a higher bar than revoke: revoking locks a device out, forgetting
 * invites a new enrolment, and the code is what keeps activation a two-person act.
 */
export const forgetDevice = (deviceId: string) =>
  api.post<void>(`/devices/${deviceId}/detach`)

export const forgetUserDevices = (userId: string) =>
  api.post<{ detached: number }>(`/devices/user/${userId}/detach`)

/** The breach button: every handset on the platform, back to enrolment. */
export const forgetAllDevices = () =>
  api.post<{ detached: number }>('/devices/detach-all')

/**
 * Clears a wrong-code lockout and reopens the window.
 *
 * Needs `device:revoke`, NOT `device:activate` — whoever used up the guesses must not be
 * able to clear their own counter and carry on three at a time. The code is neither shown
 * nor changed, so the person holding the phone still has to read it out.
 */
export const resetDeviceActivation = (deviceId: string) =>
  api.post<DeviceBinding>(`/devices/${deviceId}/reset-activation`)

export interface DeviceFingerprint {
  deviceId: string
  /** Short SHA-256 of the attested key, not the key. Null on an unattested build. */
  publicKeyDigest?: string | null
  attestation?: string | null
  /** SSAID / identifierForVendor, brand, model. Persistent per-device handles. */
  platformIdentifiers: Record<string, unknown>
  enrolledAt: string
  revealedAt: string
}

/**
 * What identifies this handset. **Every call is on the audit log.**
 *
 * A POST because it has a side effect: a GET would be prefetched, cached and retried by
 * browsers and proxies, quietly multiplying the record of who looked.
 */
export const revealDeviceFingerprint = (deviceId: string) =>
  api.post<DeviceFingerprint>(`/devices/${deviceId}/fingerprint`)
